import { defineRule, configure } from 'vee-validate';
import {
    required,
    email,
    min,
    max,
    is,
    length,
    numeric,
    integer,
    regex,
    max_value, // eslint-disable-line
    min_value // eslint-disable-line
} from '@vee-validate/rules';

import { isArray } from 'lodash';
import dictionaryService from '@/core/dictionary/dictionary.service';

const rules: Record<string, ValidationRule> = {
    required: {
        validator: required,
        label: 'global/validation/required',
    },
    email: {
        validator: email,
        label: 'global/validation/email',
    },
    confirmEmail: {
        validator: is,
        label: 'global/validation/confirmEmail',
    },
    'same-password': {
        validator: is,
        label: 'global/validation/samePassword',
    },
    'min-password': {
        validator: min,
        label: 'global/validation/minPassword',
    },
    min: {
        validator: min,
        label: 'global/validation/minLength',
    },
    max: {
        validator: max,
        label: 'global/validation/maxLength',
    },
    'min-value': {
        validator: min_value,
        label: 'global/validation/minValue',
    },
    'max-value': {
        validator: max_value,
        label: 'global/validation/maxValue',
    },
    length: {
        validator: length,
        label: 'global/validation/length',
    },
    numeric: {
        validator: numeric,
        label: 'global/validation/numeric',
    },
    integer: {
        validator: integer,
        label: 'global/validation/integer',
    },
    percentage: {
        validator: (value, params, ctx) => {
            if (value === null) {
                return true;
            }

            const parsedInput = parseInt(params[0], 10);
            const maxPercentage = isNaN(parsedInput) ? 100 : parsedInput;

            return integer(value) && min_value(value, [1]) && max_value(value, [maxPercentage]);
        },
        label: 'global/validation/percentage',
    },
    orderNumber: {
        validator: (value, params, ctx) => {
            return numeric(value) && min(value, params);
        },
        label: 'global/validation/orderNumber',
    },
    phone: {
        validator: (value, params, ctx) => {
            if (!value) {
                return true;
            }
            const countryCodePhoneRegex = /^\+\d{7,20}$/;
            return regex(value, [countryCodePhoneRegex]);
        },
        label: 'global/validation/phone',
    },
    postalCode: {
        validator: (value, countrySpecificRegEx, ctx) => {
            if (!value) {
                return true;
            }
            const jsCompatibleRegEx = new RegExp(countrySpecificRegEx);
            return regex(value, [jsCompatibleRegEx]);
        },
        label: 'global/validation/postalCode',
    },
    calenderDay: {
        validator: (value) => {
            if (!value) { return true; }
            const saneDayOfMonth = /^(([1-9])|([1-2][0-9])|(3[01]))$/;
            return regex(parseInt(value as string, 10), [saneDayOfMonth]);
        },
        label: '',
    },
    calenderMonth: {
        validator: (value) => {
            if (!value) { return true; }
            const saneMonth = /^([1-9]|1[012])$/;
            return regex(parseInt(value as string, 10), [saneMonth]);
        },
        label: '',
    },
    calenderYear: {
        validator: (value) => {
            if (!value) { return true; }
            const saneYear = /^(19|20)\d{2}$/;
            const isBeforeCurrentYear = new Date().getFullYear() > parseInt(value as string, 10);
            return isBeforeCurrentYear && regex(parseInt(value as string, 10), [saneYear]);
        },
        label: '',
    },
    conditionalRequired: {
        validator: (value, [target], ctx) => {
            return !!value || !target;
        },
        label: 'global/validation/required',
    },
};

configure({
    validateOnBlur: true, // controls if `blur` events should trigger validation with `handleChange` handler
    validateOnChange: true, // controls if `change` events should trigger validation with `handleChange` handler
    validateOnInput: false, // controls if `input` events should trigger validation with `handleChange` handler
    validateOnModelUpdate: true, // controls if `update:modelValue` events should trigger validation with `handleChange` handler
    generateMessage,
});

setupRules();

function setupRules(): void {
    Object.keys(rules).forEach((id: string) => {
        const validator = rules[id].validator;
        defineRule(id, validator);
    });
}

function generateMessage(context: FieldValidationMetaInfo): string {
    if (!context.rule) {
        return dictionaryService.get('global/validation/default', [context.field]);
    } else {
        const labelKey = rules[context.rule.name].label;
        const params = isArray(context.rule.params) ? context.rule.params : [context.rule.params];
        return dictionaryService.get(labelKey, params as string[]);
    }
}

interface ValidationRule {
    validator: ValidationRuleFunction<unknown, any>;
    label: string;
}

export type CustomInputFieldInternalValue = string | object | number | null | boolean;

// Types not exported in vee-validate
interface FieldValidationMetaInfo {
    field: string;
    value: unknown;
    form: Record<string, unknown>;
    rule?: {
        name: string;
        params?: Record<string, unknown> | unknown[];
    };
}
type ValidationRuleFunction<TValue = unknown, TParams = unknown[] | Record<string, unknown>> = (value: TValue, params: TParams, ctx: FieldValidationMetaInfo) => boolean | string | Promise<boolean | string>;
